import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable, { actionsColumn, Column } from './DataTable';
import EmptyState from './EmptyState';
import { MetricCard } from './MetricCards';
import LoadErrorBox from './ui/LoadErrorBox';
import Button from './ui/Button';
import { Modal } from './ui/Modal';
import { idColumn } from './ui/idColumn';
import { FormInput, FormSelect, FormTextarea } from './FormField';
import { useNotifications } from './NotificationProvider';
import { useExport } from './header/exporters';
import { useLoad } from '../hooks/useLoad';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { useRole } from '../hooks/useRole';
import { PROC_EVENTS, setInventoryBar, useProcurementBar } from '../hooks/useProcurementBar';
import {
  adjustStock, archiveInventoryItem, createInventoryItem, fetchInventoryItems, fetchMovements,
  INVENTORY_REFRESHED_EVENT, restoreInventoryItem, updateInventoryItem,
  type AdjustMovementType, type InventoryItem, type InventoryItemInput, type StockMovement,
} from '../services/InventoryService';
import { CURRENCY_OPTIONS } from '../utils/currency';
import { formatDecimal } from '../utils/decimal';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { procurementErrorMessage } from '../utils/procurementErrors';
import { fmtQty, qty } from './procurement/procurementLabels';
import { newIdempotencyKey } from './procurement/ReceivingPanel';
import '../styles/components/ProcurementScreens.css';

export const itemMatches = (i: InventoryItem, term: string): boolean => {
  if (!term) return true;
  return [i.name, i.item_type, i.category, i.vendor, i.description, i.notes].some(v => (v ?? '').toLowerCase().includes(term));
};

const MOVEMENT_LABELS: Record<string, string> = {
  received: 'Received', allocated: 'Allocated', adjusted: 'Adjusted', returned: 'Returned', damaged: 'Damaged', lost: 'Lost', opening: 'Opening stock',
};
const ADJUST_TYPES: Array<{ value: AdjustMovementType; label: string }> = (['adjusted', 'returned', 'damaged', 'lost', 'opening'] as AdjustMovementType[])
  .map(value => ({ value, label: MOVEMENT_LABELS[value] }));

interface ItemDraft { id: string | null; name: string; item_type: string; category: string; vendor: string; unit_price: string; currency: string; pack_size: string; description: string; notes: string }
const blankItem: ItemDraft = { id: null, name: '', item_type: '', category: '', vendor: '', unit_price: '', currency: '', pack_size: '', description: '', notes: '' };
const s = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** The body for an item. A person who may not see money never sends a price (it would overwrite the real one with nothing). */
export function itemInputFrom(d: ItemDraft, canViewFinancials: boolean): InventoryItemInput {
  const input: InventoryItemInput = {
    name: d.name.trim(), item_type: orNull(d.item_type), category: orNull(d.category), vendor: orNull(d.vendor),
    pack_size: orNull(d.pack_size), description: orNull(d.description), notes: orNull(d.notes),
  };
  if (canViewFinancials) { input.unit_price = orNull(d.unit_price); input.currency = orNull(d.currency); }
  return input;
}

const signedQty = (v: number | string): string => (qty(v) > 0 ? `+${fmtQty(v)}` : fmtQty(v));

// One stable empty list: a fresh [] on every render made the item-type effect below fire forever while loading or after a failed load.
const NO_ITEMS: InventoryItem[] = [];

const InventoryTracker: React.FC = () => {
  const { showSuccess, showError } = useNotifications();
  const { canEdit, canViewFinancials } = useRole();
  const { inventory: bar } = useProcurementBar();
  const search = useHeaderSearch().trim().toLowerCase();

  const items = useLoad(() => fetchInventoryItems({ lifecycle: bar.archived ? 'archived' : 'active' }), [bar.archived]);
  const [itemDraft, setItemDraft] = useState<ItemDraft | null>(null);
  const [itemProblem, setItemProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [stockItem, setStockItem] = useState<InventoryItem | null>(null);

  const rows = items.data ?? NO_ITEMS;
  const { setData } = items;

  useEffect(() => {
    if (bar.archived) return undefined;
    const onRefreshed = (e: Event) => {
      const list = (e as CustomEvent<{ items?: InventoryItem[] }>).detail?.items;
      if (Array.isArray(list)) setData(list);
    };
    window.addEventListener(INVENTORY_REFRESHED_EVENT, onRefreshed);
    return () => window.removeEventListener(INVENTORY_REFRESHED_EVENT, onRefreshed);
  }, [bar.archived, setData]);

  const itemTypes = useMemo(() => [...new Set(rows.map(i => i.item_type).filter((t): t is string => !!t))].sort((a, b) => a.localeCompare(b)), [rows]);
  useEffect(() => { setInventoryBar({ itemTypes }); }, [itemTypes]);

  const shown = useMemo(
    () => rows.filter(i => (!bar.itemType || i.item_type === bar.itemType) && itemMatches(i, search)),
    [rows, bar.itemType, search],
  );
  useExport('inventory', { rows: shown, ctx: undefined });

  useEffect(() => {
    const add = () => { if (canEdit) { setItemProblem(null); setItemDraft(blankItem); } };
    window.addEventListener(PROC_EVENTS.inventoryAdd, add);
    return () => window.removeEventListener(PROC_EVENTS.inventoryAdd, add);
  }, [canEdit]);

  const saveItem = async () => {
    if (!itemDraft) return;
    if (!itemDraft.name.trim()) { setItemProblem('Give the item a name.'); return; }
    if (canViewFinancials && itemDraft.unit_price.trim() && !itemDraft.currency.trim()) { setItemProblem('Choose the currency of the unit price.'); return; }
    setSaving(true); setItemProblem(null);
    try {
      const input = itemInputFrom(itemDraft, canViewFinancials);
      if (itemDraft.id) await updateInventoryItem(itemDraft.id, input); else await createInventoryItem(input);
      showSuccess('Item saved'); setItemDraft(null); await items.reload();
    } catch (err) { setItemProblem(procurementErrorMessage(err, 'The item could not be saved.')); } finally { setSaving(false); }
  };

  const toggleArchive = useCallback(async (item: InventoryItem) => {
    try {
      if (item.archived_at) await restoreInventoryItem(item.id); else await archiveInventoryItem(item.id);
      showSuccess(item.archived_at ? 'Item restored' : 'Item archived');
      await items.reload();
    } catch (err) { showError(procurementErrorMessage(err, 'The item could not be changed.')); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.reload, showError, showSuccess]);

  if (items.error) {
    return <div className="wt-container fade-in"><LoadErrorBox what="inventory" error={items.error} onRetry={items.reload} retrying={items.loading} /></div>;
  }

  const inStock = rows.filter(i => qty(i.stock) > 0).length;
  const columns: Column<InventoryItem>[] = [
    idColumn<InventoryItem>('inventoryItem'),
    { key: 'name', header: 'Item', sortable: true },
    { key: 'item_type', header: 'Type', sortable: true },
    { key: 'category', header: 'Category', sortable: true },
    { key: 'vendor', header: 'Vendor', sortable: true },
    { key: 'stock', header: 'On hand', sortable: true, align: 'right', sortValue: i => qty(i.stock), render: (_v, i) => fmtQty(i.stock) },
    ...(canViewFinancials ? [{ key: 'unit_price', header: 'Unit price', align: 'right' as const, render: (_v: unknown, i: InventoryItem) => formatDecimal(i.unit_price, i.currency) }] : []),
    { key: 'last_movement_at', header: 'Last movement', sortable: true, render: (_v, i) => (i.last_movement_at ? formatDate(i.last_movement_at, '') || i.last_movement_at : '—') },
    actionsColumn<InventoryItem>([
      { key: 'stock', icon: 'package', title: 'Stock and movements', onClick: item => setStockItem(item) },
      { key: 'edit', icon: 'edit', title: 'Edit item', show: () => canEdit, onClick: item => { setItemProblem(null); setItemDraft({ id: item.id, name: item.name, item_type: s(item.item_type), category: s(item.category), vendor: s(item.vendor), unit_price: s(item.unit_price), currency: s(item.currency), pack_size: s(item.pack_size), description: s(item.description), notes: s(item.notes) }); } },
      { key: 'archive', icon: 'trash', title: item => (item.archived_at ? 'Restore item' : 'Archive item'), show: () => canEdit, variant: item => (item.archived_at ? 'success' : 'danger'), onClick: item => void toggleArchive(item) },
    ]),
  ];

  return (
    <div className="wt-container fade-in">
      <section className="stats-grid">
        <MetricCard label={bar.archived ? 'Archived items' : 'Items'} value={rows.length} tone="pink" icon="package" />
        <MetricCard label="In stock" value={inStock} tone="green" icon="check" />
        <MetricCard label="Out of stock" value={rows.length - inStock} tone="orange" icon="alert" />
      </section>

      {!items.loading && shown.length === 0 ? (
        <EmptyState
          icon="package"
          title={rows.length === 0 ? (bar.archived ? 'No archived items' : 'No inventory items yet') : 'No items match these filters'}
          description={rows.length === 0 && !bar.archived ? 'Stock appears here as goods are received against orders, or when you add an item.' : undefined}
        />
      ) : (
        <DataTable<InventoryItem>
          data={shown}
          columns={columns}
          loading={items.loading}
          emptyMessage="No items"
          pagination={{ enabled: true, pageSize: 25 }}
          onRowClick={item => setStockItem(item)}
        />
      )}

      <Modal
        open={!!itemDraft} onClose={() => setItemDraft(null)} title={itemDraft?.id ? 'Edit item' : 'New item'} size="small"
        footer={<div className="modal-actions"><Button variant="primary" loading={saving} onClick={() => void saveItem()}>Save item</Button><Button variant="secondary" onClick={() => setItemDraft(null)}>Cancel</Button></div>}
      >
        {itemDraft && (
          <div className="proc-form-grid">
            <FormInput label="Name" required value={itemDraft.name} onChange={e => setItemDraft({ ...itemDraft, name: e.target.value })} />
            <FormInput label="Type" value={itemDraft.item_type} onChange={e => setItemDraft({ ...itemDraft, item_type: e.target.value })} />
            <FormInput label="Category" value={itemDraft.category} onChange={e => setItemDraft({ ...itemDraft, category: e.target.value })} />
            <FormInput label="Vendor" value={itemDraft.vendor} onChange={e => setItemDraft({ ...itemDraft, vendor: e.target.value })} />
            {canViewFinancials && (
              <>
                <FormInput label="Unit price" inputMode="decimal" value={itemDraft.unit_price} onChange={e => setItemDraft({ ...itemDraft, unit_price: e.target.value })} />
                <FormSelect label="Currency" value={itemDraft.currency} onChange={e => setItemDraft({ ...itemDraft, currency: e.target.value })} options={[{ value: '', label: 'Choose a currency' }, ...CURRENCY_OPTIONS]} />
              </>
            )}
            <FormInput label="Pack size" inputMode="decimal" value={itemDraft.pack_size} onChange={e => setItemDraft({ ...itemDraft, pack_size: e.target.value })} />
            <FormTextarea label="Description" rows={2} value={itemDraft.description} onChange={e => setItemDraft({ ...itemDraft, description: e.target.value })} />
            <FormTextarea label="Notes" rows={2} value={itemDraft.notes} onChange={e => setItemDraft({ ...itemDraft, notes: e.target.value })} />
            {itemProblem && <p role="alert" className="form-error-text">{itemProblem}</p>}
          </div>
        )}
      </Modal>

      {stockItem && (
        <StockModal
          item={stockItem}
          canEdit={canEdit}
          onClose={() => setStockItem(null)}
          onAdjusted={updated => { setStockItem(updated); void items.reload(); }}
        />
      )}
    </div>
  );
};

interface StockModalProps {
  item: InventoryItem;
  canEdit: boolean;
  onClose: () => void;
  onAdjusted: (item: InventoryItem) => void;
}

/** Movements of one item and the form to adjust its stock. */
export const StockModal: React.FC<StockModalProps> = ({ item, canEdit, onClose, onAdjusted }) => {
  const { showSuccess } = useNotifications();
  const moves = useLoad<StockMovement[]>(() => fetchMovements(item.id), [item.id]);
  const [delta, setDelta] = useState('');
  const [type, setType] = useState<AdjustMovementType>('adjusted');
  const [notes, setNotes] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const adjust = async () => {
    const n = Number(delta);
    if (!delta.trim() || !Number.isFinite(n) || n === 0) { setProblem('Enter a quantity other than zero. Use a minus sign to take stock out.'); return; }
    setSaving(true); setProblem(null);
    try {
      const res = await adjustStock(item.id, { quantity_delta: delta.trim(), movement_type: type, ...(notes.trim() ? { notes: notes.trim() } : {}), idempotency_key: newIdempotencyKey() });
      showSuccess('Stock adjusted');
      setDelta(''); setNotes('');
      await moves.reload();
      onAdjusted(res.item);
    } catch (err) {
      setProblem(procurementErrorMessage(err, 'The stock could not be adjusted.'));
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`${item.name}: ${fmtQty(item.stock)} on hand`} size="medium">
      {moves.error ? (
        <LoadErrorBox what="stock movements" error={moves.error} onRetry={moves.reload} retrying={moves.loading} />
      ) : !moves.data ? (
        <p className="text-subtle">Loading movements...</p>
      ) : moves.data.length === 0 ? (
        <p className="text-subtle">No stock movements yet.</p>
      ) : (
        <div className="proc-scroll">
          <table className="proc-table proc-table--compact">
            <thead><tr><th>When</th><th>Type</th><th className="proc-num">Change</th><th>Notes</th></tr></thead>
            <tbody>
              {moves.data.map(m => (
                <tr key={m.id}>
                  <td>{formatDateTime(m.occurred_at)}</td>
                  <td>{MOVEMENT_LABELS[m.movement_type] ?? m.movement_type}</td>
                  <td className="proc-num">{signedQty(m.quantity_delta)}</td>
                  <td>{m.notes ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canEdit && (
        <div className="proc-inline-form">
          <FormInput label="Change in stock" inputMode="decimal" value={delta} onChange={e => { setProblem(null); setDelta(e.target.value); }} help="Positive adds, negative takes out" />
          <FormSelect label="Reason" value={type} onChange={e => setType(e.target.value as AdjustMovementType)} options={ADJUST_TYPES} />
          <FormInput label="Notes" value={notes} onChange={e => setNotes(e.target.value)} />
          <Button variant="primary" size="sm" loading={saving} onClick={() => void adjust()}>Adjust stock</Button>
          {problem && <p role="alert" className="form-error-text">{problem}</p>}
        </div>
      )}
    </Modal>
  );
};

export default InventoryTracker;
